/**
 * 📌 Script: MatrikelService.js
 * 📂 Caminho: src/services/MatrikelService.js
 * 📝 Descrição: Utilitários para manipulação e validação de Matrikel-/Mitarbeiter-IDs
 *    no frontend. Inclui: conversões entre formato legível (LNNNNNN-D) e formato de DB
 *    (7 dígitos), cálculo de DV (Mod 11), validações e geração local da próxima matrícula
 *    via Firestore (somente leitura).
 *
 * 🔄 Relação com o Projeto:
 *  - Consumido por: MitarbeiterRegistrierung, MitarbeiterAktualisierung, AnmeldungView,
 *    StartseiteView e componentes de gestão de usuários.
 *  - Fluxos: validação de entrada no frontend, pré-visualização de matrícula gerada
 *    localmente, conversão para armazenamento.
 *
 * 📥 Entradas (Parâmetros) — resumo:
 *  - `isValidLetter(letter: String)` → Boolean
 *  - `isSixDigits(n: String|Number)` → Boolean
 *  - `isValidSevenDigits(n: String|Number)` → Boolean
 *  - `calcDV(letter: String, number6: String)` → Number|null
 *  - `buildFullMatricula(letter: String, number6: String)` → "LNNNNNN-D"
 *  - `toDbFormat(letter: String, number6: String)` → "ddddddd" (7 dígitos)
 *  - `fromDbFormat(dbValue: String)` → "LNNNNNN-D"|null
 *  - `normalizeMatricula(displayLike: String)` → "Lddddddd" (sem hífen)
 *  - `isSupremeMatricula(input: String)` → Boolean
 *  - `generateNextMatriculaLocally(letter: String)` →
 *      Promise<{ ok: boolean, sevenDigits?: string, full?: string, message?: string }>
 *
 * 📤 Saídas:
 *  - Retornos variam entre Boolean, String, Number ou objetos Promise (no caso da geração).
 *
 * 🧩 Exports:
 *  - `LETTER_LABELS`, `LETTER_DV_VALUES`, `DV_VALUE_TO_LETTER`
 *  - `isValidLetter()`, `isSixDigits()`, `isValidSevenDigits()`
 *  - `calcDV()`, `buildFullMatricula()`, `toDbFormat()`, `fromDbFormat()`,
 *    `normalizeMatricula()`, `isSupremeMatricula()`
 *  - `generateNextMatriculaLocally()`
 *
 * 🔗 Dependências:
 *  - **Internos:** usados por MitarbeiterRegistrierung, MitarbeiterAktualisierung,
 *    AnmeldungView, StartseiteView.
 *  - **Backend / Segurança:** cálculo DV e validações aqui são apenas para UX.
 *    Backend deve validar e autorizar a matrícula final. A geração local usa apenas
 *    leituras no Firestore (`getDocs`).
 *  - **Externos (NPM):** `firebase/firestore` (getFirestore, collection, query,
 *    where, orderBy, limit, startAfter, getDocs) e `@/firebaseConfig`.
 *
 * ⚠️ Observação:
 *  - Trata identificadores de funcionários (dados pessoais).
 *  - Não armazena nem manipula senhas.
 *  - Evite registrar matrículas completas em logs públicos.
 */

import app from "@/firebaseConfig";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  startAt,
  endAt,
} from "firebase/firestore";

/* =========================
 * Rótulos e Mapas de Valores
 * ========================= */

// Mapeamento de letras para rótulos (exibição em alemão)
export const LETTER_LABELS = {
  M: "Mitarbeiter",
  T: "Teilnehmer",
  A: "Azubi",
  P: "Praktikant",
  D: "Dienstleister",
  F: "Freiwilliger",
  B: "Besucher",
};

// Valores numéricos para cálculo do DV (Mod 11)
export const LETTER_DV_VALUES = {
  M: 9,
  T: 8,
  A: 7,
  P: 6,
  D: 5,
  F: 4,
  B: 3,
};

// Inverso: valor numérico → letra
export const DV_VALUE_TO_LETTER = Object.fromEntries(
  Object.entries(LETTER_DV_VALUES).map(([k, v]) => [v, k])
);

/* =========================
 * Validadores Básicos
 * ========================= */

// ✅ Letra permitida
export function isValidLetter(letter) {
  const L = String(letter || "").toUpperCase();
  return Object.prototype.hasOwnProperty.call(LETTER_DV_VALUES, L);
}

// ✅ Seis dígitos
export function isSixDigits(n) {
  return /^\d{6}$/.test(String(n || ""));
}

// ✅ Sete dígitos (formato DB)
export function isValidSevenDigits(n) {
  return /^\d{7}$/.test(String(n || ""));
}

/* =========================
 * Cálculo de DV (Mod 11)
 * ========================= */

/**
 * 🧮 Calcula o DV (Mod 11) com pesos 2..9 da direita p/ esquerda.
 * DV = 11 - (soma % 11); se 10 ou 11 → 0.
 */
export function calcDV(letter, number6) {
  const L = String(letter || "").toUpperCase();
  if (!isValidLetter(L)) return null;

  const six = String(number6 || "")
    .replace(/\D/g, "")
    .padStart(6, "0");
  const seq = [LETTER_DV_VALUES[L], ...six.split("").map((d) => +d)];

  let weight = 2;
  let sum = 0;

  for (let i = seq.length - 1; i >= 0; i--) {
    sum += seq[i] * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }

  const mod = sum % 11;
  const dvRaw = 11 - mod;
  return dvRaw === 10 || dvRaw === 11 ? 0 : dvRaw;
}

/* =========================
 * Construção/Conversão
 * ========================= */

/**
 * 🔤 Exibição legível: LNNNNNN-D
 */
export function buildFullMatricula(letter, number6) {
  const L = String(letter || "").toUpperCase();
  const N = String(number6 || "")
    .replace(/\D/g, "")
    .padStart(6, "0");

  if (!isValidLetter(L) || !isSixDigits(N)) {
    return `${L}${N}-0`;
  }

  const dv = calcDV(L, N);
  return `${L}${N}-${dv ?? 0}`;
}

/**
 * 📥 Legível (L + 6 dígitos + DV) → armazenamento (7 dígitos)
 * Ex.: M105024-7 → 9105024
 */
export function toDbFormat(letter, number6) {
  const L = String(letter || "").toUpperCase();
  if (!isValidLetter(L)) return null;

  const six = String(number6 || "")
    .replace(/\D/g, "")
    .padStart(6, "0");
  if (!isSixDigits(six)) return null;

  return `${LETTER_DV_VALUES[L]}${six}`;
}

/**
 * 📤 Armazenamento (7 dígitos) → legível (LNNNNNN-D)
 * Ex.: 9105024 → M105024-7
 */
export function fromDbFormat(dbValue) {
  const clean = String(dbValue || "")
    .replace(/\D/g, "")
    .padStart(7, "0");
  if (!isValidSevenDigits(clean)) return null;

  const val = +clean.charAt(0);
  const letter = DV_VALUE_TO_LETTER[val];
  if (!letter) return null;

  const number6 = clean.slice(1);
  return buildFullMatricula(letter, number6);
}

/**
 * 🧼 Normaliza para LNNNNNND (sem hífen)
 * Aceita entrada já normalizada ou do tipo LNNNNNN-D.
 */
export function normalizeMatricula(displayLike) {
  const s = String(displayLike || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

  if (/^[A-Z]\d{7}$/.test(s)) return s;

  const letter = s[0];
  const digits = s.slice(1).replace(/\D/g, "");

  if (/[A-Z]/.test(letter) && /^\d{7}$/.test(digits)) {
    return `${letter}${digits}`;
  }
  return s;
}

/* =========================
 * Regras Especiais
 * ========================= */

/**
 * 🔒 Trava "Deus" (matrícula soberana)
 * Bloqueia M000000 (qualquer DV) e 9000000 no DB.
 */
export function isSupremeMatricula(input) {
  const raw = String(input || "");
  const alnum = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const digits = raw.replace(/\D/g, "");

  if (/^M000000\d$/.test(alnum)) return true;
  if (/^9000000$/.test(digits)) return true;

  return false;
}

/* =========================
 * Nova Função: Geração Local
 * ========================= */

/**
 * ⚡ Gera a próxima matrícula localmente consultando o Firestore.
 * Apenas leitura no front-end para pré-visualização.
 * O back-end sempre fará validação final.
 *
 * @param {string} letter - Letra do tipo de usuário
 * @returns {Promise<{ ok: boolean, sevenDigits?: string, full?: string, message?: string }>}
 */
// Leitura crescente com startAt/endAt e 1º gap
export async function generateNextMatriculaLocally(letter) {
  // 🔒 valida letra
  const L = String(letter || "").toUpperCase();
  if (!isValidLetter(L)) {
    return { ok: false, message: "Ungültiger Benutzertyp." };
  }

  try {
    const db = getFirestore(app);
    const usersRef = collection(db, "users");

    // 📐 define faixa da letra (evita M000000)
    const low = `${LETTER_DV_VALUES[L]}000001`;
    const high = `${LETTER_DV_VALUES[L]}999999`;

    // ▶️ valor esperado; inicia no menor válido
    let expected = low;
    // ▶️ cursor de paginação
    let lastDoc = null;

    // 🔁 evita condição constante (ESLint) e laço infinito
    for (let page = 0; page < 200; page += 1) {
      // 🔎 página em ordem crescente dentro da faixa
      const q = lastDoc
        ? query(
            usersRef,
            orderBy("mitarbeiterId", "asc"),
            startAfter(lastDoc),
            endAt(high),
            limit(500)
          )
        : query(
            usersRef,
            orderBy("mitarbeiterId", "asc"),
            startAt(low),
            endAt(high),
            limit(500)
          );

      const snap = await getDocs(q);

      // 🔚 sem registros → expected é o 1º livre ou não há espaço
      if (snap.empty) {
        if (expected > high) {
          return { ok: false, message: "Keine freie Mitarbeiter-ID." };
        }
        const num6 = expected.slice(1);
        return {
          ok: true,
          sevenDigits: expected,
          full: buildFullMatricula(L, num6),
        };
      }

      for (const d of snap.docs) {
        const cur = String(d.data().mitarbeiterId || "")
          .replace(/\D/g, "")
          .padStart(7, "0");

        if (cur < low || cur > high) continue;

        if (cur !== expected) {
          const num6 = expected.slice(1);
          return {
            ok: true,
            sevenDigits: expected,
            full: buildFullMatricula(L, num6),
          };
        }

        expected = String(parseInt(expected, 10) + 1).padStart(7, "0");

        if (expected > high) {
          return { ok: false, message: "Keine freie Mitarbeiter-ID." };
        }
      }

      // ✅ se chegou ao fim da faixa nesta página, pegue a próxima livre
      if (snap.size < 500) {
        if (expected <= high) {
          const num6 = expected.slice(1);
          return {
            ok: true,
            sevenDigits: expected,
            full: buildFullMatricula(L, num6),
          };
        }
        return { ok: false, message: "Keine freie Mitarbeiter-ID." };
      }

      // ➡️ próxima página
      lastDoc = snap.docs[snap.docs.length - 1];
    }

    // 🔒 guarda de segurança para excesso de páginas
    return { ok: false, message: "Abbruch nach 200 Seiten." };
  } catch (err) {
    console.error("generateNextMatriculaLocally error:", err);
    return { ok: false, message: "Fehler beim lokalen Abruf." };
  }
}
